import React, { useState } from 'react';
import ActivityTitle from '../common/ActivityTitle';
import FullscreenVideo from '../packages/FullscreenVideo';

type VideoItem = {
  thumbnailUrl: string;
  videoUrl: string;
};

type ExperienceVideosProps = {
  videos: VideoItem[];
};

const ExperienceVideos: React.FC<ExperienceVideosProps> = ({ videos = [] }) => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);

  if (!Array.isArray(videos) || videos.length === 0) return null;

  const collapsedVideos = showAll ? videos : videos.slice(0, 4);
  const showToggleButton = videos.length > 2;
  const shouldHideButtonOnDesktop = videos.length <= 4;

  const handleThumbnailClick = (videoUrl: string) => {
    setActiveVideo(videoUrl);
  };

  const handleCloseVideo = () => {
    setActiveVideo(null);
  };

  return (
    <>
      <div className='mt-0'>
        <div className='flex items-center justify-between mb-2'>
          <ActivityTitle title='Experience Videos' className='mb-0' />
          {showToggleButton && (
            <button
              type='button'
              onClick={() => setShowAll(!showAll)}
              className={`flex items-center gap-1 text-xs text-black no-underline transition-all whitespace-nowrap ml-4 ${
                shouldHideButtonOnDesktop ? 'md:hidden' : ''
              }`}
            >
              <span>{showAll ? 'Show less' : 'Show more'}</span>
            </button>
          )}
        </div>
        <div
          className={`grid gap-3 mt-2 mb-8 max-w-3xl ${
            showAll
              ? 'grid-cols-2 md:grid-cols-4'
              : 'grid-cols-2 md:grid-cols-4'
          }`}
        >
          {collapsedVideos.map((item, index) => {
            const isHiddenOnMobile = !showAll && index >= 2;
            return (
              <div
                key={index}
                className={`${isHiddenOnMobile ? 'hidden md:block' : ''}`}
              >
                <div className='relative w-full h-[90px] md:h-[100px] rounded-xl overflow-hidden bg-gray-100 shadow-lg'>
                  {/* Prefer the video's own first frame: API thumbnails for
                      portrait videos are pre-squashed to 16:9 server-side, so
                      cropping them with object-cover cannot undo the distortion. */}
                  {item.videoUrl ? (
                    <video
                      src={item.videoUrl}
                      muted
                      playsInline
                      preload='metadata'
                      className='absolute inset-0 h-full w-full object-cover object-center pointer-events-none'
                      aria-hidden='true'
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.thumbnailUrl || '/assets/images/No-Image.jpg'}
                      alt='Video thumbnail'
                      className='absolute inset-0 h-full w-full object-cover object-center pointer-events-none'
                    />
                  )}
                  <button
                    type='button'
                    onClick={() => handleThumbnailClick(item.videoUrl)}
                    className='group absolute inset-0 z-10'
                    aria-label='Play video'
                  >
                    {/* Play Button */}
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div className='relative w-9 h-9 group-hover:scale-110 transition-transform duration-200'>
                        {/* Circle Background */}
                        <div className='absolute inset-0 rounded-full bg-black/50'></div>
                        {/* Play Triangle */}
                        <svg
                          width='36'
                          height='36'
                          viewBox='0 0 36 36'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                          className='relative z-10'
                        >
                          <polygon
                            points='14,12 24,18 14,24'
                            fill='rgba(255, 255, 255, 0.9)'
                          />
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fullscreen Video */}
      {activeVideo && (
        <FullscreenVideo src={activeVideo} onClose={handleCloseVideo} />
      )}
    </>
  );
};

export default ExperienceVideos;
